/**
 * Horarios › Ausencias (008 · T076 · US4; contracts/ui.md §1.5, spec US4 escenarios 4–8).
 *
 * - Filtros en borrador (QA-2): desde/hasta, trabajador, sede, tipo y estado (defecto "Activas"),
 *   con "Buscar", "Limpiar filtros" y "Actualizar".
 * - Tabla (Trabajador, Tipo con badge, Desde, Hasta/Horario, Nota, Estado, Origen) paginada por
 *   cursor ("Cargar más").
 * - "Registrar ausencia" y "Cancelar" con `schedules.write` (Company Admin, soporte y el Encargado
 *   de sede dentro de su alcance, que recorta la API). Una ausencia de integración se ve con
 *   candado y no se cancela aquí. Empresa suspendida ⇒ acciones deshabilitadas (solo lectura).
 *
 * Montada desde `routes/_app/horarios.tsx` a través del registro `SCHEDULE_TABS`.
 */
import { CalendarOff, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AbsenceTypeSchema, type Absence } from '@fermin/contracts';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../components/ui/card';
import { Combobox } from '../../components/ui/combobox';
import { EmptyState } from '../../components/ui/empty-state';
import { Field } from '../../components/ui/field';
import { FilterActions, FilterForm } from '../../components/ui/filter-actions';
import { Input } from '../../components/ui/input';
import { RefreshButton } from '../../components/ui/refresh-button';
import { Select, type SelectOption } from '../../components/ui/select';
import { SkeletonLines } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../components/ui/table';
import { t } from '../../i18n';
import { useAppliedFilters } from '../../lib/use-applied-filters';
import { managedLabel, ManagedSourceLabel, useIntegrationSystemName } from '../integration/managed-by';
import { scheduleErrorMessage, useWorkerOptions } from '../schedules/api';
import { formatLocalDay, timeRange, todayInZone } from '../schedules/format';
import { hasPermission, useMe } from '../shell/use-me';
import { readOnlyProps, useReadOnly } from '../shell/use-read-only';
import { useSites } from '../sites/api';

import { AbsenceFormDialog } from './absence-form-dialog';
import { absenceTypeLabel, AbsenceTypeBadge } from './absence-type-badge';
import { absencesKey, useAbsences, type AbsenceFilters, type AbsenceStatusFilter } from './api';
import { CancelAbsenceDialog } from './cancel-absence-dialog';

/** Valor centinela de "todos" en los `Select` (Radix no admite `''` como valor de ítem). */
export const ALL = 'ALL';

interface AbsenceDraft {
  from_day: string;
  to_day: string;
  worker_id: string;
  site_id: string;
  type: string;
  status: AbsenceStatusFilter;
}

const DEFAULT_DRAFT: AbsenceDraft = {
  from_day: '',
  to_day: '',
  worker_id: '',
  site_id: ALL,
  type: ALL,
  status: 'ACTIVE',
};

const STATUS_FILTERS: readonly AbsenceStatusFilter[] = ['ACTIVE', 'CANCELLED', 'ALL'];

/** Filtros de la API desde el borrador aplicado; `null` si el rango está invertido. */
export function absenceFiltersFrom(draft: AbsenceDraft): AbsenceFilters | null {
  if (draft.from_day && draft.to_day && draft.from_day > draft.to_day) {
    return null;
  }
  const type = AbsenceTypeSchema.safeParse(draft.type);
  return {
    ...(draft.from_day ? { from_day: draft.from_day } : {}),
    ...(draft.to_day ? { to_day: draft.to_day } : {}),
    ...(draft.worker_id ? { worker_id: draft.worker_id } : {}),
    ...(draft.site_id !== ALL ? { site_id: draft.site_id } : {}),
    ...(type.success ? { type: type.data } : {}),
    status: draft.status,
  };
}

export function AbsencesTab() {
  const me = useMe();
  const { readOnly } = useReadOnly();
  const canWrite = hasPermission(me.data, 'schedules.write');
  const today = todayInZone(me.data?.company?.default_timezone ?? 'America/Lima');

  const { draft, applied, update, apply, clear, dirty, isFiltered, canClear } =
    useAppliedFilters<AbsenceDraft>(DEFAULT_DRAFT);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const filters = useMemo(() => absenceFiltersFrom(applied), [applied]);
  const query = useAbsences(filters);
  const absences = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );

  const sites = useSites({ status: 'ACTIVE' });
  const workers = useWorkerOptions(draft.site_id !== ALL ? { site_id: draft.site_id } : {});
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelling, setCancelling] = useState<Absence | null>(null);

  const siteOptions: SelectOption[] = useMemo(
    () => [
      { value: ALL, label: t('absences.filters.allSites') },
      ...(sites.data ?? []).map((site) => ({ value: site.id, label: site.name })),
    ],
    [sites.data],
  );
  const typeOptions: SelectOption[] = useMemo(
    () => [
      { value: ALL, label: t('absences.filters.allTypes') },
      ...AbsenceTypeSchema.options.map((type) => ({ value: type, label: absenceTypeLabel(type) })),
    ],
    [],
  );
  const statusOptions: SelectOption[] = useMemo(
    () =>
      STATUS_FILTERS.map((status) => ({
        value: status,
        label: t(`absences.filters.status.${status}`),
      })),
    [],
  );
  const workerOptions = useMemo(
    () => [{ value: '', label: t('absences.filters.allWorkers') }, ...workers.options],
    [workers.options],
  );

  function applyFilters() {
    if (draft.from_day && draft.to_day && draft.from_day > draft.to_day) {
      setRangeError(t('absences.filters.rangeInverted'));
      return;
    }
    setRangeError(null);
    apply();
  }

  function clearFilters() {
    setRangeError(null);
    clear();
  }

  return (
    <div className="flex flex-col gap-fm-4">
      <div className="flex flex-wrap items-center justify-between gap-fm-3">
        <p className="text-fm-sm text-fm-text-secondary">{t('absences.page.description')}</p>
        <div className="flex flex-wrap items-center gap-fm-2">
          <RefreshButton query={query} pagesKey={absencesKey(filters)} />
          {canWrite ? (
            <Button
              variant="compact"
              size="md"
              icon={<Plus size={20} />}
              data-testid="absence-new"
              onClick={() => setCreateOpen(true)}
              {...readOnlyProps(readOnly)}
            >
              {t('absences.page.new')}
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        <FilterForm onApply={applyFilters} className="block">
          <fieldset className="flex flex-wrap items-end gap-fm-3">
            <legend className="sr-only">{t('absences.filters.legend')}</legend>
            <Field label={t('absences.filters.fromDay')} className="w-[170px]">
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  invalid={rangeError !== null}
                  value={draft.from_day}
                  onChange={(event) => update({ from_day: event.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('absences.filters.toDay')}
              className="w-[170px]"
              error={rangeError ?? undefined}
            >
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  value={draft.to_day}
                  onChange={(event) => update({ to_day: event.target.value })}
                />
              )}
            </Field>
            <Field label={t('absences.filters.worker')} className="w-[220px]">
              {(control) => (
                <Combobox
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.worker_id}
                  onValueChange={(value) => update({ worker_id: value })}
                  options={workerOptions}
                  placeholder={t('absences.filters.allWorkers')}
                  searchPlaceholder={t('absences.form.workerSearch')}
                />
              )}
            </Field>
            <Field label={t('absences.filters.site')} className="w-[200px]">
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.site_id}
                  onValueChange={(value) => update({ site_id: value, worker_id: '' })}
                  options={siteOptions}
                />
              )}
            </Field>
            <Field label={t('absences.filters.type')} className="w-[180px]">
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.type}
                  onValueChange={(value) => update({ type: value })}
                  options={typeOptions}
                />
              )}
            </Field>
            <Field label={t('absences.filters.statusLabel')} className="w-[160px]">
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.status}
                  onValueChange={(value) =>
                    update({
                      status: STATUS_FILTERS.find((status) => status === value) ?? 'ACTIVE',
                    })
                  }
                  options={statusOptions}
                />
              )}
            </Field>
            <FilterActions
              onSearch={applyFilters}
              dirty={dirty}
              canClear={canClear}
              onClear={clearFilters}
            />
          </fieldset>
        </FilterForm>
      </Card>

      {query.isPending && filters !== null ? <SkeletonLines lines={4} /> : null}

      {query.isError ? (
        <Card>
          <CardTitle>{t('absences.error.title')}</CardTitle>
          <CardDescription>{scheduleErrorMessage(query.error)}</CardDescription>
          <Button className="mt-fm-4" size="sm" onClick={() => void query.refetch()}>
            {t('common.buttons.retry')}
          </Button>
        </Card>
      ) : null}

      {query.isSuccess && absences.length === 0 ? (
        <EmptyState
          icon={CalendarOff}
          title={isFiltered ? t('absences.noResults.title') : t('absences.empty.title')}
          description={
            isFiltered ? t('absences.noResults.description') : t('absences.empty.description')
          }
        />
      ) : null}

      {absences.length > 0 ? (
        <>
          <Table data-testid="absences-table">
            <TableCaption>{t('absences.page.caption')}</TableCaption>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('absences.columns.worker')}</TableHeaderCell>
                <TableHeaderCell>{t('absences.columns.type')}</TableHeaderCell>
                <TableHeaderCell>{t('absences.columns.from')}</TableHeaderCell>
                <TableHeaderCell>{t('absences.columns.to')}</TableHeaderCell>
                <TableHeaderCell>{t('absences.columns.note')}</TableHeaderCell>
                <TableHeaderCell>{t('absences.columns.status')}</TableHeaderCell>
                <TableHeaderCell>{t('absences.columns.source')}</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">{t('absences.columns.actions')}</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {absences.map((absence) => (
                <AbsenceRow
                  key={absence.id}
                  absence={absence}
                  canWrite={canWrite}
                  readOnly={readOnly}
                  onCancel={() => setCancelling(absence)}
                />
              ))}
            </TableBody>
          </Table>
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common.buttons.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {canWrite ? (
        <>
          <AbsenceFormDialog open={createOpen} onOpenChange={setCreateOpen} today={today} />
          <CancelAbsenceDialog absence={cancelling} onClose={() => setCancelling(null)} />
        </>
      ) : null}
    </div>
  );
}

function AbsenceRow({
  absence,
  canWrite,
  readOnly,
  onCancel,
}: {
  absence: Absence;
  canWrite: boolean;
  readOnly: boolean;
  onCancel: () => void;
}) {
  const integration = absence.source === 'INTEGRATION';
  // 009 · US6 (T055): "Gestionado por <sistema>" (el nombre sale de `GET /me`, en caché).
  const systemName = useIntegrationSystemName();
  const cancellable = canWrite && absence.status === 'ACTIVE' && !integration;
  return (
    <TableRow data-testid="absence-row" data-status={absence.status}>
      <TableCell className="font-semibold">{absence.worker.name}</TableCell>
      <TableCell>
        <AbsenceTypeBadge type={absence.type} partial={absence.partial} />
      </TableCell>
      <TableCell className="fm-tabular">{formatLocalDay(absence.start_day)}</TableCell>
      <TableCell className="fm-tabular">
        {absence.partial && absence.start_time && absence.end_time
          ? timeRange(absence.start_time, absence.end_time)
          : formatLocalDay(absence.end_day)}
      </TableCell>
      <TableCell className="max-w-[220px]">
        {absence.note ? (
          <span className="block truncate" title={absence.note}>
            {absence.note}
          </span>
        ) : (
          <span className="text-fm-text-secondary">{t('absences.table.noNote')}</span>
        )}
      </TableCell>
      <TableCell>
        <span className="flex flex-col gap-fm-1">
          {absence.status === 'ACTIVE' ? (
            <Badge variant="activa">{t('absences.status.ACTIVE')}</Badge>
          ) : (
            <Badge variant="cancelada">{t('absences.status.CANCELLED')}</Badge>
          )}
          {absence.cancel_reason ? (
            <span className="text-fm-xs text-fm-text-secondary">
              {t('absences.table.cancelReason', { reason: absence.cancel_reason })}
            </span>
          ) : null}
        </span>
      </TableCell>
      <TableCell>
        <ManagedSourceLabel source={absence.source} systemName={systemName} />
      </TableCell>
      <TableCell>
        {cancellable ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid="absence-cancel-open"
            aria-label={t('absences.table.cancelLabel', {
              type: absenceTypeLabel(absence.type, absence.partial),
              worker: absence.worker.name,
            })}
            onClick={onCancel}
            {...readOnlyProps(readOnly)}
          >
            {t('absences.table.cancel')}
          </Button>
        ) : integration && absence.status === 'ACTIVE' ? (
          <span className="sr-only">
            {t('absences.managed.locked', { system: managedLabel(systemName) })}
          </span>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
